import { useEffect, useRef } from "react";
import mqtt, { MqttClient } from "mqtt";
import { MQTT_WS_URL } from "../app/constants/config.constant";

export type CheckinDeviceRecord = {
  deviceId: string;
  name: string;
  location: string;
  note?: string;
  appVersion?: string;
  appVersionCode?: number;
  lastIp?: string;
  lastSeenAt?: string | null;
  lastScanAt?: string | null;
  lastScanEmployeeCode?: string;
  lastScanEmployeeName?: string;
  status: "online" | "offline" | "unknown";
  socketConnected?: boolean;
  mqttConnected?: boolean;
  alertEnabled?: boolean;
  accountUsername?: string;
  hasDeviceAccount?: boolean;
  hasRecoveryCode?: boolean;
  recoveryCodeGeneratedAt?: string | null;
};

export type DeviceMqttPayload = {
  type: "device_status" | "device_offline" | "device_updated";
  device: CheckinDeviceRecord;
  receivedAt?: string;
};

type UseDeviceStatusMqttOptions = {
  mqttTopic?: string | null;
  enabled?: boolean;
  onUpdate: (payload: DeviceMqttPayload) => void;
};

export const useDeviceStatusMqtt = ({
  mqttTopic,
  enabled = true,
  onUpdate,
}: UseDeviceStatusMqttOptions) => {
  const onUpdateRef = useRef(onUpdate);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    if (!enabled || !mqttTopic || !MQTT_WS_URL) {
      return undefined;
    }

    let client: MqttClient | null = null;
    let disposed = false;

    client = mqtt.connect(MQTT_WS_URL, {
      reconnectPeriod: 5000,
      connectTimeout: 10000,
      clean: true,
    });

    client.on("connect", () => {
      if (disposed || !client) {
        return;
      }
      client.subscribe(mqttTopic, { qos: 1 });
    });

    client.on("message", (_topic, message) => {
      try {
        const payload = JSON.parse(message.toString()) as DeviceMqttPayload;
        if (payload?.device?.deviceId) {
          onUpdateRef.current(payload);
        }
      } catch (error) {
        console.error("[device-mqtt] invalid payload", error);
      }
    });

    client.on("error", (error) => {
      console.error("[device-mqtt] connection error", error.message);
    });

    return () => {
      disposed = true;
      client?.end(true);
    };
  }, [enabled, mqttTopic]);
};

export const mergeDeviceUpdate = (
  devices: CheckinDeviceRecord[],
  payload: DeviceMqttPayload
) => {
  const updated = payload.device;
  const exists = devices.some((item) => item.deviceId === updated.deviceId);
  const next = exists
    ? devices.map((item) =>
        item.deviceId === updated.deviceId ? { ...item, ...updated } : item
      )
    : [...devices, updated];

  return [...next].sort((left, right) =>
    String(left.name || left.deviceId).localeCompare(
      String(right.name || right.deviceId)
    )
  );
};
